/**
 * Global table styling tokens + sx presets.
 * Import `commonTableStyles` (or `tableStyles`) in every table UI.
 */

export const TABLE_TOKENS = {
  accent: "#5546FF",
  accentSoft: "#EDE7F6",
  accentMuted: "#6C3CE9",
  ink: "#1B1340",
  text: "#364152",
  muted: "#697586",
  border: "#EAE8F2",
  headerBg: "#F7F6FB",
  rowHover: "#F9F8FD",
  paper: "#FFFFFF",
  radius: "12px",
  font: "Roboto, sans-serif",
};

export const commonTableStyles = {
  paper: {
    backgroundColor: TABLE_TOKENS.paper,
    color: TABLE_TOKENS.ink,
    transition: "box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    overflow: "hidden",
    marginTop: "16px",
    border: `1px solid ${TABLE_TOKENS.border}`,
    borderRadius: TABLE_TOKENS.radius,
    boxShadow: "0 1px 2px rgba(27, 19, 64, 0.04)",
  },
  buttonBox: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    mb: 0.5,
  },
  chartDimension: {
    height: "385px",
    width: "100%",
  },
  sno_col: {
    maxWidth: "70px !important",
    minWidth: "70px !important",
    textAlign: "center !important",
  },
  headerRow: {
    "& th": {
      textAlign: "left",
    },
  },
  tableHeader: {
    backgroundColor: TABLE_TOKENS.headerBg,
    "& th": {
      padding: "14px 16px",
      fontFamily: TABLE_TOKENS.font,
      fontWeight: 600,
      fontSize: "0.8125rem",
      lineHeight: "18px",
      letterSpacing: "0.02em",
      textTransform: "uppercase",
      color: TABLE_TOKENS.accent,
      whiteSpace: "nowrap",
      borderBottom: `1px solid ${TABLE_TOKENS.border}`,
      backgroundColor: TABLE_TOKENS.headerBg,
    },
  },
  searchAndFilter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
  },
  not_tableHeader: {
    "& th": {
      padding: "14px 16px",
      fontFamily: TABLE_TOKENS.font,
      fontWeight: 600,
      fontSize: "0.8125rem",
      lineHeight: "18px",
      color: TABLE_TOKENS.accent,
      whiteSpace: "nowrap",
      backgroundColor: TABLE_TOKENS.headerBg,
    },
  },
  table: {
    fontFamily: TABLE_TOKENS.font,
    display: "table",
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: "0px",
    marginBottom: "0px",
  },
  row: {
    transition: "background-color 160ms ease",
    "&:hover": {
      backgroundColor: TABLE_TOKENS.rowHover,
    },
    "& td": {
      padding: "14px 16px",
      maxWidth: "220px",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      fontFamily: TABLE_TOKENS.font,
      fontWeight: 400,
      fontSize: "0.875rem",
      lineHeight: "20px",
      color: TABLE_TOKENS.text,
      textAlign: "left",
      borderBottom: `1px solid ${TABLE_TOKENS.border}`,
    },
    "&:last-of-type td": {
      borderBottom: "none",
    },
    "& td:nth-last-of-type(1)": {
      "& svg": {
        color: TABLE_TOKENS.accent,
        cursor: "pointer",
        fontSize: "20px",
        margin: "0 2px",
        transition: "opacity 160ms ease, transform 160ms ease",
        "&:hover": {
          opacity: 0.85,
          transform: "scale(1.05)",
        },
      },
    },
  },
  sortIcon: { paddingLeft: "3px", height: "11px", cursor: "pointer" },
  noDataCell: {
    fontFamily: TABLE_TOKENS.font,
    fontWeight: 500,
    fontSize: "0.95rem",
    lineHeight: "24px",
    textAlign: "center",
    padding: "40px 16px",
    color: TABLE_TOKENS.muted,
  },
  noData: { display: "flex", justifyContent: "center" },
  dwnIcnStyl: {
    cursor: "pointer",
    width: "14px",
    height: "14px",
    verticalAlign: "middle",
    marginLeft: "4px",
    color: TABLE_TOKENS.accent,
    transition: "transform 0.25s ease",
  },
  rotateIcon: {
    transform: "rotate(180deg)",
    transition: "transform 0.25s ease",
  },
  paginationContainer: {
    padding: "8px 16px",
    borderTop: `1px solid ${TABLE_TOKENS.border}`,
    backgroundColor: "#FCFBFE",
    "& .MuiToolbar-root.MuiTablePagination-toolbar": {
      padding: "0",
      minHeight: "52px",
    },
    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
      fontSize: "0.8125rem",
      color: TABLE_TOKENS.muted,
    },
  },
  statusColumn: {
    height: "26px",
    minWidth: "78px",
    px: 1,
    borderRadius: "999px",
    display: "inline-flex",
    justifyContent: "center",
    alignItems: "center",
    color: "#fff",
    fontSize: "0.75rem",
    fontWeight: 600,
    margin: "auto",
  },
  icon: {
    height: "30px",
    width: "30px",
  },
  profileIcon: {
    height: "30px",
    width: "30px",
    borderRadius: "50%",
  },
  tableHeader_cnt_algn: {
    backgroundColor: TABLE_TOKENS.headerBg,
    "& th": {
      padding: "14px 16px",
      fontFamily: TABLE_TOKENS.font,
      fontWeight: 600,
      fontSize: "0.8125rem",
      lineHeight: "18px",
      color: TABLE_TOKENS.accent,
      whiteSpace: "nowrap",
      textAlign: "center",
      backgroundColor: TABLE_TOKENS.headerBg,
      borderBottom: `1px solid ${TABLE_TOKENS.border}`,
    },
  },
  row_cnt_algn: {
    transition: "background-color 160ms ease",
    "&:hover": {
      backgroundColor: TABLE_TOKENS.rowHover,
    },
    "& td": {
      padding: "14px 16px",
      maxWidth: "200px",
      minWidth: "140px",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      fontFamily: TABLE_TOKENS.font,
      fontWeight: 400,
      fontSize: "0.875rem",
      lineHeight: "20px",
      color: TABLE_TOKENS.text,
      textAlign: "center",
      borderBottom: `1px solid ${TABLE_TOKENS.border}`,
    },
    "& td:nth-last-of-type(1)": {
      "& svg": {
        color: TABLE_TOKENS.accent,
        cursor: "pointer",
        fontSize: "20px",
        margin: "0 2px",
      },
    },
  },
  row_notification: {
    transition: "background-color 160ms ease",
    "&:hover": {
      backgroundColor: TABLE_TOKENS.rowHover,
    },
    "& td": {
      padding: "14px 16px",
      maxWidth: "200px",
      minWidth: "140px",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      fontFamily: TABLE_TOKENS.font,
      fontWeight: 400,
      fontSize: "0.875rem",
      lineHeight: "20px",
      color: TABLE_TOKENS.text,
      textAlign: "center",
      borderBottom: `1px solid ${TABLE_TOKENS.border}`,
    },
    "& td:nth-last-of-type(1)": {
      padding: "14px 16px",
      maxWidth: "350px",
      minWidth: "200px",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      fontFamily: TABLE_TOKENS.font,
      fontWeight: 400,
      fontSize: "0.875rem",
      lineHeight: "20px",
      textAlign: "center",
      "& svg": {
        color: TABLE_TOKENS.accent,
        cursor: "pointer",
        fontSize: "20px",
        margin: "0 2px",
      },
    },
  },
  row_earnings: {
    transition: "background-color 160ms ease",
    "&:hover": {
      backgroundColor: TABLE_TOKENS.rowHover,
    },
    "& td": {
      padding: "14px 16px",
      maxWidth: "200px",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      fontFamily: TABLE_TOKENS.font,
      fontWeight: 400,
      fontSize: "0.875rem",
      lineHeight: "20px",
      color: TABLE_TOKENS.text,
      textAlign: "center",
      borderBottom: `1px solid ${TABLE_TOKENS.border}`,
    },
    "& td:nth-last-of-type(1)": {
      maxWidth: "350px",
      "& svg": {
        color: TABLE_TOKENS.accent,
        cursor: "pointer",
        fontSize: "20px",
        margin: "0 2px",
      },
    },
  },
  icon_box: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  action_table_cell_container: {
    gap: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  topPGHeading: {
    margin: "20px 0px",
  },
  selectDropdown: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    fontSize: "0.8125rem",
    color: TABLE_TOKENS.text,
    fontWeight: "400",
    borderRadius: "10px",
    "& > div": {
      background: "none",
      color: "black",
      borderRadius: "10px",
      fontSize: "0.8125rem",
      fontWeight: "400",
    },
    "& fieldset": {
      color: "black",
      borderRadius: "10px",
      borderColor: TABLE_TOKENS.border,
    },
  },
  top_container: {
    width: "100%",
    height: "50px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "20px",
  },
  dual_report_top_container: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "20px",
    paddingTop: "0px",
    height: "unset",
    marginTop: "0px",
  },
  top_container_info: {
    height: "50px",
    width: "380px",
    border: `1px solid ${TABLE_TOKENS.border}`,
    borderRadius: "10px",
    margin: "0px 0px 10px 30px",
    background: "#F8FAFC",
  },
  top_cnt_int: {
    height: "inherit",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "20px",
  },
  top_container_filter: {
    width: "120px",
    height: "45px",
    boxShadow: "none",
    fontSize: "30px",
    borderRadius: "10px",
    margin: "0px 30px 10px 0px",
  },
  img: {
    width: "12px",
    height: "12px",
    marginRight: "5px",
    marginBottom: "2px",
  },
  categoryImg: {
    width: "24px",
    height: "24px",
    marginRight: "5px",
  },
  tips_container: {
    height: "70px",
    marginRight: "70px",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: "20px",
    paddingTop: 0,
  },
  tableHeadingTxt: {
    display: "flex",
    alignItems: "center",
    fontWeight: 600,
    color: TABLE_TOKENS.ink,
  },
  tableHeadingSubTxt: {
    color: TABLE_TOKENS.accent,
    fontWeight: "600",
    marginLeft: "10px",
  },
  filterLabelTxt: {
    left: "-15px",
  },
  /** Shared look for order-table filter panel fields (select + autocomplete) */
  filterFieldRoot: {
    width: "100%",
    minWidth: 0,
    mt: 2,
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: "#fff",
      fontSize: "0.875rem",
      color: TABLE_TOKENS.text,
      "& fieldset": {
        borderColor: TABLE_TOKENS.border,
      },
      "&:hover fieldset": {
        borderColor: TABLE_TOKENS.accent,
      },
      "&.Mui-focused fieldset": {
        borderColor: TABLE_TOKENS.accent,
        borderWidth: "1px",
      },
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.875rem",
      color: TABLE_TOKENS.muted,
      "&.Mui-focused": {
        color: TABLE_TOKENS.accent,
      },
    },
    "& .MuiSelect-select, & .MuiAutocomplete-input": {
      fontSize: "0.875rem",
      color: TABLE_TOKENS.text,
    },
  },
  HeadBtns: {
    padding: "auto 50px",
    height: "38px",
    minWidth: "140px",
    textAlign: "center",
    fontFamily: TABLE_TOKENS.font,
    borderRadius: "10px",
  },
  toolbarAddBtn: {
    height: "36px",
    minWidth: "auto",
    px: 2.25,
    ml: 2.5,
    borderRadius: "10px",
    textTransform: "none",
    fontFamily: TABLE_TOKENS.font,
    fontWeight: 600,
    fontSize: "0.875rem",
    letterSpacing: "0.01em",
    boxShadow: "none",
    background: `linear-gradient(135deg, ${TABLE_TOKENS.accent} 0%, ${TABLE_TOKENS.accentMuted} 100%)`,
    "&:hover": {
      boxShadow: "0 4px 12px rgba(85, 70, 255, 0.28)",
      background: `linear-gradient(135deg, ${TABLE_TOKENS.accentMuted} 0%, ${TABLE_TOKENS.accent} 100%)`,
    },
  },
  headingTitleText: {
    fontFamily: TABLE_TOKENS.font,
    fontSize: "0.8125rem !important",
    fontWeight: "600 !important",
  },
  tableChip: {
    fontSize: "0.8125rem",
    fontWeight: "600",
    borderRadius: "8px",
  },
};

/** Alias for clearer imports */
export const tableStyles = commonTableStyles;

export default commonTableStyles;
